import React from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";
import mainImg from "../../static/media/main.png";
import logo from "../../static/media/logoGRMPNG.webp";

const C = {
  dark: "#0C1F2F",
  navy: "#0057A8",
  navyDark: "#003D73",
  gold: "#FFCD00",
  goldDark: "#E6B800",
  teal: "#5BC8AF",
  gray: "#F5F7FA",
  white: "#ffffff",
};

const Icon = ({
  path,
  size = 16,
  stroke = C.teal,
  strokeWidth = 2,
  extra = null,
}) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{
      width: size,
      height: size,
      fill: "none",
      stroke,
      strokeWidth,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      flexShrink: 0,
    }}
  >
    {path}
    {extra}
  </Box>
);

const BADGES = [
  {
    label: "Envíos nacionales",
    icon: (
      <Icon
        path={
          <>
            <rect x="1" y="3" width="15" height="13" rx="2" />
            <path d="M16 8h4l3 3v5h-7V8z" />
            <circle cx="5.5" cy="18.5" r="2.5" />
            <circle cx="18.5" cy="18.5" r="2.5" />
          </>
        }
        size={13}
      />
    ),
  },
  {
    label: "Garantía incluida",
    icon: (
      <Icon
        path={<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />}
        size={13}
      />
    ),
  },
  {
    label: "Entrega puntual",
    icon: (
      <Icon
        path={
          <>
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </>
        }
        size={13}
      />
    ),
  },
];

export default function HeroSection() {
  const navigate = useNavigate();

  return (
    <Box
      id="inicio"
      component={motion.section}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.9 }}
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        pt: { xs: "62px", md: "70px" },
        minHeight: "100vh",
        background: C.dark,
        boxSizing: "border-box",
      }}
    >
      <Box
        component="img"
        src={mainImg}
        alt=""
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center top",
          zIndex: 0,
        }}
      />
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(120deg, rgba(12,31,47,0.88) 0%, rgba(12,31,47,0.65) 55%, rgba(12,31,47,0.30) 100%)",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          height: "3px",
          background: `linear-gradient(90deg, ${C.navy} 0%, ${C.gold} 50%, ${C.teal} 100%)`,
          position: "relative",
          zIndex: 3,
          flexShrink: 0,
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          display: "flex",
          alignItems: "center",
          width: "100%",
          maxWidth: "1140px",
          mx: "auto",
          px: { xs: "20px", sm: "32px", md: "40px" },
          py: { xs: "48px", md: "64px" },
          pb: { xs: "120px", md: "160px" }, // Damos espacio extra para el gradiente
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ maxWidth: { xs: "100%", md: "580px" }, width: "100%" }}>
          <Typography
            variant="h1"
            component={motion.h1}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            sx={{
              color: C.white,
              fontWeight: 900,
              fontSize: { xs: "28px", sm: "38px", md: "52px" },
              lineHeight: 1.08,
              letterSpacing: "-.02em",
              mb: "20px",
            }}
          >
            Tu aliado{" "}
            <Box component="span" sx={{ color: C.white }}>
              estratégico
            </Box>
            <br />
            <Box component="span" sx={{ color: C.white }}>
              todo en un lugar
            </Box>
          </Typography>

          <Typography
            component={motion.p}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.28 }}
            sx={{
              color: "rgba(255,255,255,0.72)",
              fontSize: { xs: "14px", md: "16px" },
              lineHeight: 1.7,
              maxWidth: "440px",
              mb: "36px",
            }}
          >
            Distribución de material eléctrico y ferretero con envíos a nivel
            nacional. Precios accesibles, entrega puntual y soporte técnico
            real.
          </Typography>

          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
              mb: "32px",
            }}
          >
            <Box
              component="button"
              onClick={() =>
                window.open(
                  "https://api.whatsapp.com/send/?phone=593987724998&text&type=phone_number&app_absent=0",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "9px",
                background: C.gold,
                border: "none",
                borderRadius: "50px",
                px: { xs: "22px", md: "28px" },
                py: "14px",
                fontSize: "13px",
                fontWeight: 800,
                color: C.dark,
                cursor: "pointer",
                letterSpacing: ".05em",
                textTransform: "uppercase",
                transition: "background .2s, transform .15s",
                "&:hover": {
                  background: C.goldDark,
                  transform: "translateY(-2px)",
                },
                "&:active": { transform: "translateY(0)" },
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{
                  width: 17,
                  height: 17,
                  fill: "none",
                  stroke: C.dark,
                  strokeWidth: 2.2,
                  strokeLinecap: "round",
                }}
              >
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 015.11 12.84 19.79 19.79 0 012.04 4.18 2 2 0 014 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </Box>
              Cotizar ahora
            </Box>

            <Box
              component="button"
              onClick={() => {
                navigate("/productos");
                window.scrollTo(0, 0);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: "50px",
                px: { xs: "20px", md: "26px" },
                py: "14px",
                fontSize: "13px",
                fontWeight: 600,
                color: C.white,
                cursor: "pointer",
                transition: "background .2s, transform .15s",
                backdropFilter: "blur(4px)",
                "&:hover": {
                  background: "rgba(255,255,255,0.20)",
                  transform: "translateY(-2px)",
                },
                "&:active": { transform: "translateY(0)" },
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{
                  width: 16,
                  height: 16,
                  fill: "none",
                  stroke: "currentColor",
                  strokeWidth: 2,
                  strokeLinecap: "round",
                }}
              >
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </Box>
              Ver productos
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: "8px", md: "10px" },
              flexWrap: "wrap",
            }}
          >
            {BADGES.map((b) => (
              <Box
                key={b.label}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  borderRadius: "50px",
                  px: "14px",
                  py: "6px",
                }}
              >
                {b.icon}
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "11px",
                    fontWeight: 600,
                    letterSpacing: ".04em",
                  }}
                >
                  {b.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            position: "absolute",
            right: { md: "60px", lg: "80px" },
            top: "50%",
            transform: "translateY(-50%)",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              width: 130,
              height: 130,
              borderRadius: "50%",
              border: "2px solid rgba(255,205,0,0.40)",
              background: "rgba(12,31,47,0.60)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 0 0 12px rgba(255,205,0,0.07), 0 0 0 24px rgba(0,87,168,0.06)",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Eléctrica GRM"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              sx={{
                width: "84px",
                height: "auto",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                color: C.white,
                fontWeight: 900,
                fontSize: "17px",
                letterSpacing: ".06em",
                textTransform: "uppercase",
                lineHeight: 1,
              }}
            >
              Eléctrica
            </Typography>
            <Typography
              sx={{
                color: C.gold,
                fontWeight: 700,
                fontSize: "10px",
                letterSpacing: ".26em",
                textTransform: "uppercase",
                mt: "3px",
              }}
            >
              GRM
            </Typography>
          </Box>
          <Box
            sx={{
              width: "64px",
              height: "3px",
              borderRadius: "2px",
              background: `linear-gradient(90deg, ${C.navy}, ${C.gold}, ${C.teal})`,
            }}
          />
        </Box>
      </Box>

      {/* Degradado alargado suave conectado al fondo */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: { xs: "120px", md: "180px" },
          background: `linear-gradient(180deg, rgba(245,247,250,0) 0%, ${C.gray} 100%)`,
          zIndex: 2,
          pointerEvents: "none", // Para que no bloquee clics
        }}
      />
    </Box>
  );
}
